import React, { useState } from 'react';
import {
  Button,
  makeStyles,
  tokens,
  Text,
  Card,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
  Link,
} from '@fluentui/react-components';
import { ArrowSyncRegular, DismissRegular, ErrorCircleFilled } from '@fluentui/react-icons';
import { clearAuthProvider } from '../config';
import JobLogo from '../assets/job.svg';
import { useHeaderStyles } from './Header/Header.styles';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #e8f4fc 0%, #ffffff 50%, #f5f5f5 100%)',
  },
  messageBarContainer: {
    borderRadius: '4px',
    margin: '16px 24px',
    whiteSpace: 'normal',
    '& *': {
      whiteSpace: 'normal',
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: '24px',
  },
  infoBox: {
    width: '100%',
    maxWidth: '440px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    boxShadow: tokens.shadow4,
  },
  infoIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
    marginTop: '2px',
    width: '18px',
    height: '18px',
  },
  infoText: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.5',
  },
  infoLink: {
    color: tokens.colorBrandForeground1,
    textDecoration: 'none',
    fontWeight: 600,
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  card: {
    width: '100%',
    maxWidth: '440px',
    padding: '40px 48px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    boxShadow: tokens.shadow16,
  },
  logo: {
    width: '46px',
    height: '46px',
    marginBottom: '20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    marginBottom: '8px',
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
  },
  primaryButton: {
    width: '100%',
    justifyContent: 'center',
    padding: '12px 24px',
    borderRadius: '6px',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: 600,
    fontSize: '14px',
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  buttonIcon: {
    marginRight: '8px',
  },
});

interface ErrorPageProps {
  errorMessage?: string;
}

export const ErrorPage: React.FC<ErrorPageProps> = ({ errorMessage }) => {
  const styles = useStyles();
  const headerStyles = useHeaderStyles();
  const [showError, setShowError] = useState(true);

  // Base URL for navigation - handles apps hosted under a subdirectory
  const baseUrl = process.env.PUBLIC_URL || window.location.origin;

  const handleTryAgain = () => {
    // Clear all storage and auth state
    // This clears MSAL cache in sessionStorage
    sessionStorage.clear();
    localStorage.clear();
    clearAuthProvider();

    // Redirect to app root - MSAL will initiate fresh B2C login
    // since there are no cached accounts
    window.location.href = baseUrl;
  };

  const handleEmployeeSignIn = () => {
    // Clear all storage and auth state
    sessionStorage.clear();
    localStorage.clear();
    clearAuthProvider();

    // Redirect to corporate auth path
    window.location.href = `${baseUrl}/auth/corporate`;
  };

  const handleDismissError = () => {
    setShowError(false);
  };

  return (
    <div className={styles.page}>
      {/* Header Banner - reusing Header styles */}
      <div className={headerStyles.header}>
        <div className={headerStyles.brand}>
          <img src={JobLogo} alt="Microsoft" width="32" height="32" />
          <span className={headerStyles.title}>New Hire Hub</span>
        </div>
      </div>

      {/* Error Bar */}
      {errorMessage && showError && (
        <MessageBar intent="error" className={styles.messageBarContainer} icon={<ErrorCircleFilled />}>
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {errorMessage.split('\n\n').map((paragraph, index) => {
              const lines = paragraph.split('\n');
              const isBulletList = lines.every((line) => line.startsWith('- '));
              const isBold = paragraph.startsWith('**') && paragraph.endsWith('**');

              if (isBulletList) {
                return (
                  <ul key={index} style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                    {lines.map((line) => (
                      <li key={line}>{line.slice(2)}</li>
                    ))}
                  </ul>
                );
              }

              return (
                <span key={index} style={{ display: 'block', marginTop: index > 0 ? '8px' : '0' }}>
                  {isBold ? <strong>{paragraph.slice(2, -2)}</strong> : paragraph}
                </span>
              );
            })}
          </MessageBarBody>
          <MessageBarActions
            containerAction={
              <Button
                appearance="transparent"
                icon={<DismissRegular />}
                onClick={handleDismissError}
                aria-label="Dismiss error"
              />
            }
          />
        </MessageBar>
      )}

      <div className={styles.container}>
        {/* Info box for Microsoft Employees */}
        <div className={styles.infoBox}>
          <svg
            className={styles.infoIcon}
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="9" cy="9" r="9" fill="#0078d4" />
            <rect x="8" y="4" width="2" height="2" rx="1" fill="white" />
            <rect x="8" y="7.5" width="2" height="5.5" rx="1" fill="white" />
          </svg>
          <Text className={styles.infoText}>
            If you are a Microsoft Employee,{' '}
            <Link className={styles.infoLink} onClick={handleEmployeeSignIn}>
              Sign in here
            </Link>
            .
          </Text>
        </div>

        <Card className={styles.card}>
          {/* Microsoft Logo */}
          <img src={JobLogo} alt="" aria-hidden="true" className={styles.logo} />

          {/* Title */}
          <Text className={styles.title}>Something went wrong</Text>

          {/* Subtitle */}
          <Text className={styles.subtitle}>
            We couldn't find your onboarding data. Please try again or contact support if the problem persists.
          </Text>

          {/* Try Again Button */}
          <div className={styles.buttonsContainer}>
            <Button
              className={styles.primaryButton}
              appearance="primary"
              icon={<ArrowSyncRegular className={styles.buttonIcon} />}
              onClick={handleTryAgain}
            >
              Try again
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ErrorPage;
